import { useMemo } from 'react';
import {
  resolveCreditsMeter,
  resolveDailyLimitMeter,
  useCredits,
  type CreditsMeter,
  type DailyLimitMeter,
} from './use-credits';
import { useCreditPlanContext } from './use-credit-plan-context';
import { creditsStateBiFields } from './credits-state-bi';
import {
  useCreditsWidgetSim,
  useCreditsWidgetSimDelay,
  useCreditsWidgetSimMessageCount,
  SIM_DELAY_MESSAGE_THRESHOLD,
  type CreditsWidgetSim,
} from './credits-widget-sim';

/**
 * The one source of truth for whether — and how — the low/out-of-credits widgets show. The drawer
 * card, the inner-screen banner and the in-chat card + sticky bar all read THIS, so no two surfaces can
 * disagree about whether the account is low, out, or fine, or which pool they measured.
 *
 * `none` — fine, or the widgets are gated off, or the balance is unknown. The widgets render nothing.
 * `low`  — 70%+ of the relevant pool is spent (but some remains). Nudge to upgrade / buy more.
 * `out`  — the relevant pool is exhausted (0 left). Harder stop.
 */
export type CreditsWidgetLevel = 'none' | 'low' | 'out';

/**
 * Which pool the state was measured against — and, because free plans are metered daily while paid
 * plans are metered monthly, WHICH copy the widget speaks. `daily` = the FREE plan's per-day ceiling
 * (the BFF sent a `dailyLimit`), whose copy nudges to a Premium plan and names no plan/date. `monthly`
 * = a PAID plan's period pool (Max+ included), whose copy names the plan and its renewal date. There
 * is no monthly-free case: a free plan is always daily-metered.
 */
export type CreditsWidgetPool = 'daily' | 'monthly';

/** The used-percent at or above which the state is "low". A product constant, deliberately not a
 *  request field — mirrors the native `LOW_CREDITS_THRESHOLD_PCT` twin. */
export const LOW_CREDITS_THRESHOLD_PCT = 70;

export interface CreditsWidgetState {
  level: CreditsWidgetLevel;
  pool: CreditsWidgetPool;
  /** Free (unpaid) plan. Its MONTHLY copy names no plan/date, and its CTA reads "Upgrade". (Daily is
   *  always the free plan too, so daily copy never depends on this.) */
  isFreePlan: boolean;
  /** Top ("Max+") tier. Its monthly copy is the date-free "Add credits…" body; CTA reads "Add Credits". */
  isMaxPlus: boolean;
  /**
   * Whole-number percent of the relevant pool SPENT — the menu card's bar width. Always shows the real
   * value (≈70+ when low), forced to 100 when out (the pool is exhausted, so the bar is full). 0 when
   * `level` is `none`.
   */
  usedPct: number;
  /** Credits remaining in the relevant pool (0 when out). */
  remaining: number;
  /** Credits SPENT in the relevant pool, for the "{used} of {cap}" line above the bar. NOT clamped to
   *  `cap` (an over-limit day reads "105 of 100"); 0 when `level` is `none`. */
  used: number;
  /** The relevant pool's allotment (monthly `cap` / daily `limit`), for that same line. 0 when unknown. */
  cap: number;
  /** The account's plan name, for the monthly "…on your {planName} plan" copy. Empty on daily/free. */
  planName: string;
  /** Formatted renewal date for the monthly body, or null → the `.noDate` body. Null on daily/free. */
  renewalDate: string | null;
}

/** What `creditsWidgetCopyKeys` returns: the title/body keys plus the values to interpolate into them. */
export interface CreditsWidgetCopy {
  titleKey: string;
  bodyKey: string;
  /** Interpolation values for the monthly (paid/Max+) copy; empty on daily. */
  params: { planName?: string; renewalDate?: string };
}

/**
 * The title/body i18n keys (and interpolation params) for a state. Pure so it is unit-tested.
 *
 *  - `daily` → the free plan's daily copy (no plan name / no date). Daily is always the free plan.
 *  - `monthly` splits three ways by tier:
 *     · Max+   → the shared monthly TITLE (naming the plan) + a single date-free "Add credits…" body.
 *     · free   → its OWN title + body, naming NO plan and NO date ("…out of monthly credits").
 *     · paid   → the plan-named title + a body naming the renewal date (the `.noDate` body otherwise).
 *
 * The CTA label also varies by tier (see `useCreditsWidgetCta`). Never called for `none`.
 */
export function creditsWidgetCopyKeys(state: CreditsWidgetState): CreditsWidgetCopy {
  const lvl = state.level === 'out' ? 'out' : 'low';
  if (state.pool === 'daily') {
    return { titleKey: `credits.widget.daily.${lvl}.title`, bodyKey: `credits.widget.daily.${lvl}.body`, params: {} };
  }
  if (state.isMaxPlus) {
    // One date-free body for both levels; the level still selects the title.
    return { titleKey: `credits.widget.monthly.${lvl}.title`, bodyKey: 'credits.widget.monthly.maxplus.body', params: { planName: state.planName } };
  }
  if (state.isFreePlan) {
    return { titleKey: `credits.widget.monthly.free.${lvl}.title`, bodyKey: `credits.widget.monthly.free.${lvl}.body`, params: {} };
  }
  return {
    titleKey: `credits.widget.monthly.${lvl}.title`,
    bodyKey: state.renewalDate ? `credits.widget.monthly.${lvl}.body` : `credits.widget.monthly.${lvl}.body.noDate`,
    params: { planName: state.planName, renewalDate: state.renewalDate ?? '' },
  };
}

/** Format a renewal date (ISO) for the monthly body, or null when absent/unparseable → `.noDate` copy. */
export function formatRenewalDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export interface CreditsWidgetInputs {
  /** Today's ceiling as a meter, or null when the account is under no per-day cap. */
  dailyMeter: DailyLimitMeter | null;
  /** The period (monthly) meter, or null when the balance is unknown. */
  periodMeter: CreditsMeter | null;
  /** Free (unpaid) plan — picks the free monthly copy + the "Upgrade" CTA. */
  isFreePlan: boolean;
  /** Top ("Max+") tier — the date-free "Add credits…" body + the "Add Credits" CTA. */
  isMaxPlus: boolean;
  /** The account's plan name, for the monthly copy. */
  planName: string;
  /** Formatted renewal date for the monthly copy, or null → the `.noDate` body. */
  renewalDate: string | null;
}

/** The plan facts carried onto every classified state (only rendered on monthly). */
type PlanCtx = Pick<CreditsWidgetState, 'isFreePlan' | 'isMaxPlus' | 'planName' | 'renewalDate'>;

const NONE: CreditsWidgetState = { level: 'none', pool: 'monthly', isFreePlan: true, isMaxPlus: false, usedPct: 0, remaining: 0, used: 0, cap: 0, planName: '', renewalDate: null };

function classify(pool: CreditsWidgetPool, usedPct: number, remaining: number, used: number, cap: number, ctx: PlanCtx): CreditsWidgetState {
  const pct = Math.min(100, Math.max(0, usedPct));
  // Out first: an exhausted pool is "out" even if a rounding quirk left usedPct just under 100, and the
  // bar is drawn FULL there rather than at the real percent (product: "when out — totally full").
  if (remaining <= 0) return { level: 'out', pool, usedPct: 100, remaining: 0, used, cap, ...ctx };
  if (pct >= LOW_CREDITS_THRESHOLD_PCT) return { level: 'low', pool, usedPct: pct, remaining, used, cap, ...ctx };
  return { ...NONE, pool, usedPct: pct, remaining, used, cap, ...ctx };
}

/**
 * Decide the widget state from the two meters. Pure, so every branch (pool selection, the 70%/0
 * thresholds, the gate) is unit-tested without React.
 *
 * Pool rule: use the DAILY meter when the account has one (a free/enforced account under a per-day
 * ceiling — the BFF only sends `dailyLimit` then), otherwise the MONTHLY period meter. That is exactly
 * the product rule "free → daily, premium → monthly, and fall back to monthly when no daily applies",
 * because the daily meter exists precisely for the accounts the daily rule is meant for.
 *
 * A missing period meter (unknown balance) yields `none`: there is nothing honest to show, the same
 * fail-closed reading every other credits surface takes.
 */
export function computeCreditsWidgetState(inputs: CreditsWidgetInputs): CreditsWidgetState {
  const ctx: PlanCtx = { isFreePlan: inputs.isFreePlan, isMaxPlus: inputs.isMaxPlus, planName: inputs.planName, renewalDate: inputs.renewalDate };
  const daily = inputs.dailyMeter;
  if (daily) return classify('daily', daily.usedPct, daily.remaining, daily.used, daily.limit, ctx);
  const period = inputs.periodMeter;
  if (!period || period.usedPct === null) return NONE;
  return classify('monthly', period.usedPct, period.balance, period.used, period.cap, ctx);
}

/** A sample renewal date (~2 weeks out) so the dev sim's monthly states preview the WITH-date copy. */
function simRenewalDate(): string {
  const d = new Date();
  d.setDate(d.getDate() + 14);
  return formatRenewalDate(d.toISOString()) ?? '';
}

/** Map a dev simulator choice onto a concrete widget state (sample numbers). Dev-only. The monthly
 *  variants differ only in tier (free / paid / Max+), which changes the copy + the CTA. */
export function simToCreditsWidgetState(sim: CreditsWidgetSim): CreditsWidgetState {
  const free = { isFreePlan: true, isMaxPlus: false, planName: '', renewalDate: null };
  const paid = { isFreePlan: false, isMaxPlus: false, planName: 'Pro', renewalDate: simRenewalDate() };
  const maxPlus = { isFreePlan: false, isMaxPlus: true, planName: 'Max+', renewalDate: null };
  switch (sim) {
    case 'low-daily':
      return { level: 'low', pool: 'daily', usedPct: LOW_CREDITS_THRESHOLD_PCT, remaining: 30, used: 70, cap: 100, ...free };
    case 'out-daily':
      return { level: 'out', pool: 'daily', usedPct: 100, remaining: 0, used: 100, cap: 100, ...free };
    case 'low-monthly-free':
      return { level: 'low', pool: 'monthly', usedPct: LOW_CREDITS_THRESHOLD_PCT, remaining: 300, used: 700, cap: 1000, ...free };
    case 'out-monthly-free':
      return { level: 'out', pool: 'monthly', usedPct: 100, remaining: 0, used: 1000, cap: 1000, ...free };
    case 'low-monthly':
      return { level: 'low', pool: 'monthly', usedPct: LOW_CREDITS_THRESHOLD_PCT, remaining: 300, used: 700, cap: 1000, ...paid };
    case 'out-monthly':
      return { level: 'out', pool: 'monthly', usedPct: 100, remaining: 0, used: 1000, cap: 1000, ...paid };
    case 'low-monthly-maxplus':
      return { level: 'low', pool: 'monthly', usedPct: LOW_CREDITS_THRESHOLD_PCT, remaining: 300, used: 700, cap: 1000, ...maxPlus };
    case 'out-monthly-maxplus':
      return { level: 'out', pool: 'monthly', usedPct: 100, remaining: 0, used: 1000, cap: 1000, ...maxPlus };
  }
}

/**
 * The live low/out-of-credits state for the current account. Built on the shared `useCredits()` cache,
 * so it reacts to mid-conversation spend on its own (the `individuals-credits` duplexer push).
 *
 * A dev-tools simulator (`useCreditsWidgetSim`) substitutes the credit DATA (so the widgets can be
 * previewed on any account). Inert without a dev-tools grant.
 *
 * Kept as a thin alias of `useCreditsState` (its historical role was to AND that state with the
 * now-graduated `symphonyOutOrLowCreditsWidgetsWeb` rollout flag) so its many consumers need no edit.
 */
export function useCreditsWidgetState(): CreditsWidgetState {
  return useCreditsState();
}

/**
 * The account's credit state — the SINGLE source of truth for the low/out-of-credits widgets. BI reads
 * THIS directly (`useCreditsBiFields`) so a personal-sale / credits click reports the same credit
 * situation everywhere. The dev simulator still applies here (dev-only; inert in production, where no
 * sim is ever set). Mirrors native `creditsState`.
 */
export function useCreditsState(): CreditsWidgetState {
  const credits = useCredits();
  const sim = useCreditsWidgetSim();
  const simDelay = useCreditsWidgetSimDelay();
  const simMsgCount = useCreditsWidgetSimMessageCount();
  const { isFreePlan, isMaxPlus, planName, renewalDateIso } = useCreditPlanContext();
  const daily = resolveDailyLimitMeter(credits);
  const period = resolveCreditsMeter(credits);
  // Keyed on the primitives the state depends on, so the returned object is stable across renders that
  // did not move the balance — consumers don't re-render on unrelated credits reads.
  return useMemo(() => {
    if (sim) {
      // "Appear after N messages" mode: hold the state back until enough messages have been sent, to
      // reproduce credits running out mid-conversation.
      if (simDelay && simMsgCount < SIM_DELAY_MESSAGE_THRESHOLD) return NONE;
      return simToCreditsWidgetState(sim);
    }
    return computeCreditsWidgetState({
      dailyMeter: daily,
      periodMeter: period,
      isFreePlan,
      isMaxPlus,
      planName,
      renewalDate: formatRenewalDate(renewalDateIso),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sim, simDelay, simMsgCount, isFreePlan, isMaxPlus, planName, renewalDateIso, daily?.usedPct, daily?.remaining, period?.usedPct, period?.balance]);
}

/**
 * The credits-state BI additional fields (`credits_state` / `credits_pool` / `plan_tier`) attached to
 * every personal-sale / credits CTA event, from the FT-independent `useCreditsState` — see
 * `creditsStateBiFields`. One source of truth so all three events agree across all feature-flag states.
 */
export function useCreditsBiFields(): Record<string, string> {
  const s = useCreditsState();
  return creditsStateBiFields({ level: s.level, pool: s.pool, isFreePlan: s.isFreePlan, isMaxPlus: s.isMaxPlus });
}
